import {useGetNewGames} from "@hooks/gamesList/useGetNewGames";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {Carousel, CarouselGameCard, HeadingCarousel, normalizeCarouselGame} from "@/components";
import useIsMobile from "@/hooks/useIsMobile";
import {useAuthStore} from "@/store/useAuth.store";
import {shuffle} from "@/util/arrayUtils";
import {getProviderDisplayName} from "@/util/providerNames";
import styles from "./recent-games.styles.module.scss";

export const RecentGames = ({provider}) => {
  const {t} = useTranslation();
  const {provider: providerParam, category: categoryParam} = useParams();
  const location = useLocation();
  const routeContext = React.useMemo(() => {
    const getSegment = prefix => {
      const segment = location.pathname.split(prefix)[1];
      if (!segment) return "";
      return decodeURIComponent(segment.split("/")[0] || "");
    };

    const providerFromPath = providerParam || getSegment("/games/provider/");
    const categoryFromPath = categoryParam || getSegment("/games/category/");

    if (categoryFromPath) return {type: "category", value: categoryFromPath};
    if (providerFromPath) return {type: "provider", value: providerFromPath};
    return {type: "none", value: ""};
  }, [location.pathname, providerParam, categoryParam]);

  const effectiveProvider =
    provider && provider !== "all" ? provider : routeContext.type === "provider" ? routeContext.value : "";
  const {data} = useGetNewGames(effectiveProvider || undefined);
  const recentGames = data?.data?.data || [];
  const navigate = useNavigate();
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const mobile = useIsMobile({breakpoint: 426});
  const [carouselApi, setCarouselApi] = useState(null);

  const isProviderPage = routeContext.type === "provider" && Boolean(effectiveProvider);
  const providerDisplayName = isProviderPage ? getProviderDisplayName(effectiveProvider).toUpperCase() : "";
  const viewAllLink =
    routeContext.type === "category"
      ? `/games/category/${String(routeContext.value).toLowerCase()}#all-games`
      : isProviderPage
        ? `/games/provider/${String(effectiveProvider).toLowerCase()}#all-games`
        : "/gameslist/all#all-games";

  const randomGames = React.useMemo(() => shuffle(recentGames), [recentGames]);
  const cardsCount = mobile ? 12 : 16;
  const visibleGames = React.useMemo(() => randomGames.slice(0, cardsCount), [randomGames, cardsCount]);

  const handleGameClick = React.useCallback(
    (provider, gameId) => {
      if (authStatus === "authenticated") {
        navigate(`/games/${provider}/${gameId}`);
      } else {
        handleShowAuthModal();
      }
    },
    [authStatus, navigate, handleShowAuthModal]
  );

  return (
    <div
      id="recentGames"
      className={styles.recentContainer}
      style={{marginTop: "20px"}}
    >
      <HeadingCarousel
        title={isProviderPage ? t("recentTitleProvider", {provider: providerDisplayName}) : t("recentTitle")}
        subtitle={isProviderPage ? t("recentSubTitleProvider", {provider: providerDisplayName}) : t("recentSubTitle")}
        icon="📢"
        viewAllLink={viewAllLink}
        showArrowsOnMobile
        onPrev={carouselApi?.scrollPrev}
        onNext={carouselApi?.scrollNext}
      />

      <Carousel
        autoScroll={false}
        onInit={setCarouselApi}
        renderItems={() =>
          visibleGames.map((game, idx) => {
            const normalized = normalizeCarouselGame(game);
            return (
              <div key={game.id || idx}>
                <CarouselGameCard
                  name={normalized.name}
                  icon={normalized.icon}
                  providerLabel={normalized.providerLabel}
                  onClick={() => handleGameClick(normalized.provider, normalized.id)}
                />
              </div>
            );
          })
        }
      />
    </div>
  );
};
